import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Back from "../../components/button/back";

const TampilanAbsen = () => {
  const navigate = useNavigate();

  // ================= PROFILE =================
  const [user, setUser] = useState(null);
  const [datakepsek, setDataKepsek] = useState([]);
  const [sekolah, setSekolah] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // ================= ABSENSI =================
  const [absensi, setAbsensi] = useState([]);

  // ================= DETAIL MODAL =================
  const [showDetail, setShowDetail] = useState(false);
  const [selectedSiswa, setSelectedSiswa] = useState(null);

  // ================= FILTER =================
  const [filterTahun, setFilterTahun] = useState("");
  const [filterSemester, setFilterSemester] = useState("");
  const [filterKelas, setFilterKelas] = useState("");
  const [filterRombel, setFilterRombel] = useState("");

  // ================= FORMAT TANGGAL (HANYA TANGGAL) =================
  const onlyTanggal = (dateString) => {
    if (!dateString) return "";
    return dateString.split("T")[0]; // YYYY-MM-DD
  };

  // ================= Download =================

  const handleDownloadPDF = async () => {
    const element = document.getElementById("Print-area");

    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: 2, // kualitas tajam
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let position = 0;

    // jika konten lebih dari 1 halaman
    if (imgHeight <= pageHeight) {
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    } else {
      let heightLeft = imgHeight;

      while (heightLeft > 0) {
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        position -= pageHeight;

        if (heightLeft > 0) {
          pdf.addPage();
        }
      }
    }

    pdf.save(
      `Daftar-Hadir-${filterKelas}-${filterRombel}-${filterTahun}-S${filterSemester}.pdf`
    );
  };

  // ================= AMBIL PROFILE =================
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        setUser(res.data);
        setFilterKelas(res.data.kelas);
        setFilterRombel(res.data.rombel);
      } catch (err) {
        console.error("Gagal mengambil profile", err);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  /* ================= KEPSEK ================= */
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}api/auth/user-images-and-roles`)
      .then((res) =>
        setDataKepsek(res.data.filter((u) => u.jabatan === "Kepala Sekolah"))
      )
      .catch(() => "Gagal mengambil data kepala sekolah");
  }, []);

  /* ================= DATA SEKOLAH ================= */
  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/datasekolah/all`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSekolah(res.data || []);
      } catch (err) {
        console.error("Gagal mengambil data sekolah", err);
      }
    };
    fetchSchoolData();
  }, []);

  const getTanggalIndonesia = () => {
    const now = new Date();

    const bulan = [
      "Januari",
      "Februari",
      "Maret",
      "April",
      "Mei",
      "Juni",
      "Juli",
      "Agustus",
      "September",
      "Oktober",
      "November",
      "Desember",
    ];

    return `${now.getDate()} ${bulan[now.getMonth()]} ${now.getFullYear()}`;
  };

  // ================= HELPER: AMBIL TAHUN & SEMESTER TERBARU =================
  const getLatestTahunSemester = (data) => {
    if (!data.length) return { tahun: "", semester: "" };

    const sorted = [...data].sort((a, b) => {
      if (a.tahun !== b.tahun) return Number(b.tahun) - Number(a.tahun);
      return Number(b.semester) - Number(a.semester);
    });

    return {
      tahun: sorted[0].tahun,
      semester: sorted[0].semester,
    };
  };

  // ================= AMBIL & REKAP ABSENSI =================
  useEffect(() => {
    const fetchAbsensi = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/absensi/getall`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const allData = res.data;

        // 🔥 AUTO ISI TAHUN & SEMESTER TERBARU (HANYA JIKA KOSONG)
        if (!filterTahun && !filterSemester) {
          const latest = getLatestTahunSemester(allData);
          if (latest.tahun && latest.semester) {
            setFilterTahun(latest.tahun);
            setFilterSemester(latest.semester);
            return; // tunggu state update, fetch akan jalan ulang
          }
        }

        const absensifilter = allData.filter(
          (a) =>
            a.kelas === filterKelas &&
            a.rombel === filterRombel &&
            (!filterTahun || a.tahun === filterTahun) &&
            (!filterSemester || a.semester === filterSemester)
        );

        // ===== REKAP PER SISWA + DETAIL TANGGAL =====
        const siswaMap = new Map();

        absensifilter.forEach((record) => {
          const tanggal = onlyTanggal(record.tanggal);

          record.siswa.forEach((s) => {
            const siswaId = s.siswaId._id;

            if (!siswaMap.has(siswaId)) {
              siswaMap.set(siswaId, {
                siswaId,
                namaSiswa: s.namaSiswa,
                sakit: 0,
                izin: 0,
                tanpa_keterangan: 0,
                detail: {
                  sakit: [],
                  izin: [],
                  tanpa_keterangan: [],
                },
              });
            }

            const curr = siswaMap.get(siswaId);

            if (s.sakit) {
              curr.sakit += s.sakit;
              curr.detail.sakit.push(tanggal);
            }

            if (s.izin) {
              curr.izin += s.izin;
              curr.detail.izin.push(tanggal);
            }

            if (s.tanpa_keterangan) {
              curr.tanpa_keterangan += s.tanpa_keterangan;
              curr.detail.tanpa_keterangan.push(tanggal);
            }

            siswaMap.set(siswaId, curr);
          });
        });

        setAbsensi(Array.from(siswaMap.values()));
      } catch (err) {
        console.error("Gagal mengambil absensi", err);
      }
    };

    if (filterKelas && filterRombel) {
      fetchAbsensi();
    } else {
      setAbsensi([]);
    }
  }, [filterKelas, filterRombel, filterTahun, filterSemester]);

  // ================= SUMMARY =================
  const summary = useMemo(() => {
    return absensi.reduce(
      (acc, cur) => {
        acc.sakit += cur.sakit;
        acc.izin += cur.izin;
        acc.tanpa_keterangan += cur.tanpa_keterangan;
        return acc;
      },
      { sakit: 0, izin: 0, tanpa_keterangan: 0 }
    );
  }, [absensi]);

  // ================= GUARD =================
  if (loadingProfile) return <p className="p-6">Loading profile...</p>;

  if (!user || user.role !== "guru") {
    return <p className="p-6 text-red-500">Halaman ini hanya untuk guru</p>;
  }

  const routeId = user._id;

  // ================= UI =================
  return (
    <div className="p-6 space-y-6">
      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-bold">Absensi Kelas</h1>
        <p className="text-gray-600">
          Kelas {filterKelas} Rombel {filterRombel}
        </p>
      </div>

      {/* MENU */}
      <div className="flex gap-3">
        <button
          onClick={() =>
            navigate(`/addabsensi/${routeId}`, {
              state: { kelas: filterKelas, rombel: filterRombel },
            })
          }
          className="px-4 py-2 rounded-lg text-white bg-blue-600"
        >
          ➕ Tambah Absen
        </button>

        <button
          onClick={() =>
            navigate(`/absensi/edit/${routeId}`, {
              state: { kelas: filterKelas, rombel: filterRombel },
            })
          }
          className="px-4 py-2 rounded-lg text-white bg-yellow-500"
        >
          ✏️ Edit Absen
        </button>

        <button
          onClick={handleDownloadPDF}
          className="px-4 py-2 rounded-lg text-white bg-green-600"
        >
          📄 Download PDF
        </button>
      </div>

      {/* FILTER */}
      <div className="grid md:grid-cols-2 gap-4">
        <input
          placeholder="Tahun (opsional)"
          value={filterTahun}
          onChange={(e) => setFilterTahun(e.target.value)}
          className="input"
        />
        <select
          value={filterSemester}
          onChange={(e) => setFilterSemester(e.target.value)}
          className="input"
        >
          <option value="">Pilih Semester</option>
          <option value="1">Semester 1</option>
          <option value="2">Semester 2</option>
        </select>
      </div>

      {/* SUMMARY */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl shadow text-center">
          <p>Sakit</p>
          <p className="text-3xl text-red-500 font-bold">{summary.sakit}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow text-center">
          <p>Izin</p>
          <p className="text-3xl text-yellow-500 font-bold">{summary.izin}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow text-center">
          <p>Tanpa Keterangan</p>
          <p className="text-3xl font-bold">{summary.tanpa_keterangan}</p>
        </div>
      </div>

      {/* TABEL */}
      <div
        id="Print-area"
        className="bg-white px-4 py-5 absolute -left-[9999px] top-0 w-[794px]"
      >
        {/* KOP */}
        <div className="text-center mb-6 text-sm">
          <h1 className="text-xl font-bold uppercase">Daftar Hadir Murid</h1>
          <div className="flex justify-between text-sm mb-4 px-5">
            <table>
              <tbody>
                <tr>
                  <td className="font-semibold pr-2 flex justify-start">
                    Kelas
                  </td>
                  <td className="pr-2">:</td>
                  <td>{filterKelas || "-"}</td>
                </tr>
                <tr>
                  <td className="font-semibold pr-2">Rombel</td>
                  <td className="pr-2">:</td>
                  <td>{filterRombel || "-"}</td>
                </tr>
              </tbody>
            </table>

            <table>
              <tbody>
                <tr>
                  <td className="font-semibold pr-2 flex justify-start">
                    Tahun
                  </td>
                  <td className="pr-2">:</td>
                  <td>{filterTahun || "-"}</td>
                </tr>
                <tr>
                  <td className="font-semibold pr-2">Semester</td>
                  <td className="pr-2">:</td>
                  <td className="flex justify-start">
                    {filterSemester || "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <hr className="mt-4 border-black" />
        </div>
        <div className="bg-white rounded-xl shadow overflow-auto">
          <table className="w-full border">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 pb-3">No</th>
                <th className="border px-4 pb-3">Nama</th>
                <th className="border px-4 pb-3">Sakit</th>
                <th className="border px-4 pb-3">Izin</th>
                <th className="border px-4 pb-3">Tanpa Ket.</th>
                {/* <th className="border px-4 py-2">Aksi</th> */}
              </tr>
            </thead>
            <tbody>
              {absensi.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                absensi.map((s, index) => (
                  <tr key={s.siswaId}>
                    <td className="border px-4 py-2">{index + 1}</td>
                    <td className="border px-4 py-2">{s.namaSiswa}</td>
                    <td className="border px-4 py-2">{s.sakit}</td>
                    <td className="border px-4 py-2">{s.izin}</td>
                    <td className="border px-4 py-2">{s.tanpa_keterangan}</td>
                    {/* <td className="border px-4 py-2 text-center">
                      <button
                        onClick={() => {
                          setSelectedSiswa(s);
                          setShowDetail(true);
                        }}
                        className="px-3 py-1 bg-blue-500 text-white rounded"
                      >
                        Detail
                      </button>
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex justify-between mb-4 text-center px-10">
            <div className="text-sm">
              <div className="text-left inline-block">
                <br />
                <p>Mengetahui</p>
                <p>Kepala Sekolah</p>
                <br />
                <br />
                <br />
                <div className="absolute border-b border-black pb-1">
                  {datakepsek?.[0]?.name}
                </div>
                <br />
                <p className="font-semibold ">NIP. {datakepsek?.[0]?.NIP}</p>
              </div>
            </div>

            <div className="text-sm ">
              <div className="text-left inline-block">
                <p>
                  {sekolah?.[0]?.ttDsekolah}, {getTanggalIndonesia()}
                </p>
                <br />
                <p>Guru Mata Pelajaran</p>
                <br />
                <br />
                <br />
                <div className="absolute border-b border-black pb-1">
                  {user?.name}
                </div>
                <br />
                <p className="font-semibold">NIP. {user?.NIP}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className=" bg-white p-6">
        {/* KOP */}

        <div className="bg-white rounded-xl shadow overflow-auto">
          <table className="w-full border">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">No</th>
                <th className="border px-4 py-2">Nama</th>
                <th className="border px-4 py-2">Sakit</th>
                <th className="border px-4 py-2">Izin</th>
                <th className="border px-4 py-2">Tanpa Ket.</th>
                <th className="border px-4 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {absensi.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                absensi.map((s, index) => (
                  <tr key={s.siswaId}>
                    <td className="border px-4 py-2">{index + 1}</td>
                    <td className="border px-4 py-2">{s.namaSiswa}</td>
                    <td className="border px-4 py-2">{s.sakit}</td>
                    <td className="border px-4 py-2">{s.izin}</td>
                    <td className="border px-4 py-2">{s.tanpa_keterangan}</td>
                    <td className="border px-4 py-2 text-center">
                      <button
                        onClick={() => {
                          setSelectedSiswa(s);
                          setShowDetail(true);
                        }}
                        className="px-3 py-1 bg-blue-500 text-white rounded"
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div
        className="flex justify-center mt-6 cursor-pointer"
        onClick={() => navigate(`/profile/${routeId}`)}
      >
        <Back />
      </div>

      {/* MODAL DETAIL */}
      {showDetail && selectedSiswa && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white max-w-lg w-full p-6 rounded-xl space-y-4">
            <h2 className="text-xl font-bold">
              Detail Absensi – {selectedSiswa.namaSiswa}
            </h2>

            {["sakit", "izin", "tanpa_keterangan"].map((key) => (
              <div key={key}>
                <p className="font-semibold capitalize">
                  {key.replace("_", " ")}
                </p>
                {selectedSiswa.detail[key].length ? (
                  <ul className="list-disc ml-6">
                    {selectedSiswa.detail[key].map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">Tidak ada</p>
                )}
              </div>
            ))}

            <div className="text-right">
              <button
                onClick={() => setShowDetail(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TampilanAbsen;
